import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import gsap from "gsap";
import { MotionPathPlugin } from "gsap/MotionPathPlugin";
import { useSelector } from "react-redux";
import { selectMode } from "../app/appSlice"; // get theme
import BrushSVG from "../images/paintbrush.svg";

gsap.registerPlugin(MotionPathPlugin);

const Wrapper = styled.div`
    position: fixed;
    inset: 0;
    z-index: 9999;
    pointer-events: none;
    background: transparent;
`;

export default function PaintReveal() {
    const svgRef = useRef(null);
    const pathRef = useRef(null);
    const maskStrokeRef = useRef(null);

    const theme = useSelector(selectMode); // "light" | "dark"
    const overlayColor = theme === "light" ? "white" : "black";

    // brush size depending on screen
    const isMobile = window.innerWidth < 768;
    const brushSize = isMobile ? 250 : 500;

    useEffect(() => {
        const svg = svgRef.current;
        const path = pathRef.current;
        const maskStroke = maskStrokeRef.current;
        if (!svg || !path || !maskStroke) return;

        const w = window.innerWidth;
        const h = window.innerHeight;

        // zig-zag path
        const rows = 6;
        const gapY = h / rows;
        const jitter = 80;
        const rand = () => (Math.random() - 0.5) * jitter;

        let d = `M 0 0`;
        for (let i = 0; i < rows; i++) {
            const y = gapY * (i + 1);
            if (i % 2 === 0) {
                d += ` L ${w + rand()} ${y + rand()}`;
            } else {
                d += ` L ${0 + rand()} ${y + rand()}`;
            }
        }

        path.setAttribute("d", d);
        maskStroke.setAttribute("d", d);
        maskStroke.setAttribute("stroke-width", Math.max(w, h) / rows + 250);

        // stroke drawing prep
        const length = maskStroke.getTotalLength();
        gsap.set(maskStroke, { strokeDasharray: length, strokeDashoffset: length + 50 });

        const tl = gsap.timeline({
            defaults: { ease: "sine.inOut" },
            onComplete: () => {
                gsap.to(svg, {
                    duration: 0.6,
                    opacity: 0,
                    onComplete: () => svg.remove(),
                });
            },
        });

        tl.to(maskStroke, { strokeDashoffset: 0, duration: 6 }, 0);

        tl.to(
            "#brush",
            {
                duration: 6,
                motionPath: {
                    path: path,
                    align: path,
                    autoRotate: true,
                    alignOrigin: [0.5, 0.3],
                },
                y: -30,
            },
            0
        );

        // wobble
        gsap.to("#brush", {
            rotation: "+=5",
            yoyo: true,
            repeat: -1,
            duration: 0.25,
            ease: "sine.inOut",
        });

        return () => tl.kill();
    }, [theme]); // re-run when theme changes

    return (
        <Wrapper>
            <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
                preserveAspectRatio="none"
            >
                <defs>
                    <mask
                        id="overlayMask"
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width={window.innerWidth}
                        height={window.innerHeight}
                    >
                        <rect width="100%" height="100%" fill="white" />
                        <path
                            id="maskStroke"
                            ref={maskStrokeRef}
                            d=""
                            stroke="black"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            fill="none"
                        />
                    </mask>
                </defs>

                <path id="revealPath" ref={pathRef} d="" stroke="transparent" fill="none" />

                {/* overlay color depends on theme */}
                <rect
                    x="0"
                    y="0"
                    width="100%"
                    height="100%"
                    fill={overlayColor}
                    mask="url(#overlayMask)"
                />

                <image
                    id="brush"
                    href={BrushSVG}
                    width={brushSize}
                    height={brushSize}
                    x={-brushSize / 2}
                    y={-brushSize / 2}
                    preserveAspectRatio="xMidYMid meet"
                    style={{ pointerEvents: "none" }}
                />
            </svg>
        </Wrapper>
    );
}
